import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { VisiteFormSheet } from '../components/visitescsps/VisiteFormSheet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, Binoculars, CalendarBlank, MagnifyingGlass,
  PencilSimple, Trash, ArrowLeft, CheckCircle, Clock, Warning
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  chantier_id: '', date_visite: '', type_visite: 'inspection',
  objet: '', participants: [], observations_text: '', actions_requises: '',
  status: 'planifiee'
};

export function VisitesCSPSPage() {
  const navigate = useNavigate();
  const [visites, setVisites] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChantier, setFilterChantier] = useState('all');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        axios.get(`${API}/csps/visites`, { withCredentials: true }),
        axios.get(`${API}/chantiers?type=csps`, { withCredentials: true })
      ]);
      setVisites(vRes.data || []);
      setChantiers((cRes.data || []).filter(c => c && c.id && c.name));
    } catch (_err) { setVisites([]); setChantiers([]); }
    finally { setLoading(false); }
  }, [setVisites, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM, chantier_id: chantiers[0]?.id || '' });
    setIsSheetOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      chantier_id: v.chantier_id || '', date_visite: v.date_visite || '',
      type_visite: v.type_visite || 'inspection', objet: v.objet || '',
      participants: v.participants || [], observations_text: v.observations_text || '',
      actions_requises: v.actions_requises || '', status: v.status || 'planifiee'
    });
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/csps/visites/${editing.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/csps/visites`, formData, { withCredentials: true });
      }
      setIsSheetOpen(false);
      fetchData();
    } catch (_err) { toast.error('Erreur sauvegarde visite'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/csps/visites/${id}`, { withCredentials: true });
      setDeleteConfirm(null);
      fetchData();
    } catch (_err) { toast.error('Erreur suppression visite'); }
  };

  const getChantierName = (id) => (chantiers || []).find(c => c && c.id === id)?.name || 'Chantier inconnu';

  const getStatusIcon = (s) => {
    if (s === 'realisee') return <CheckCircle size={20} className="text-emerald-500" weight="fill" />;
    if (s === 'annulee') return <Warning size={20} className="text-red-500" weight="fill" />;
    return <Clock size={20} className="text-amber-500" weight="fill" />;
  };

  const getStatusLabel = (s) => {
    const map = { planifiee: 'Planifiee', realisee: 'Realisee', annulee: 'Annulee' };
    return map[s] || s;
  };

  const getTypeBadge = (t) => {
    const map = {
      inspection: { l: 'Inspection', c: 'bg-blue-100 text-blue-700' },
      chantier: { l: 'Visite chantier', c: 'bg-emerald-100 text-emerald-700' },
      coordination: { l: 'Coordination', c: 'bg-purple-100 text-purple-700' },
      accueil: { l: 'Accueil', c: 'bg-amber-100 text-amber-700' },
    };
    const d = map[t] || { l: t, c: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${d.c}`}>{d.l}</span>;
  };

  const filtered = visites.filter(v => {
    const matchSearch = !searchTerm || (v.objet || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchChantier = filterChantier === 'all' || v.chantier_id === filterChantier;
    return matchSearch && matchChantier;
  });

  if (loading) {
    return <AppShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="csps-visites-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/csps/chantiers')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]" data-testid="csps-visites-title">Visites & Inspections</h1>
              <p className="text-[#4B5563] mt-1">{visites.length} visite(s) au total</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-[#FF6B00] hover:bg-orange-700" data-testid="new-visite-btn"
            disabled={chantiers.length === 0}>
            <Plus size={20} className="mr-2" /> Nouvelle visite
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input placeholder="Rechercher..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-14"
              data-testid="visite-search" />
          </div>
          <Select value={filterChantier} onValueChange={setFilterChantier}>
            <SelectTrigger className="h-14 w-full sm:w-64" data-testid="visite-filter-chantier">
              <SelectValue placeholder="Tous les chantiers" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous les chantiers</SelectItem>
              {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="grid gap-4">
          {filtered.length > 0 ? filtered.map(v => (
            <Card key={v.id} className="chantix-card" data-testid={`visite-card-${v.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="pt-1">{getStatusIcon(v.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-[#111827] truncate">{v.objet}</h3>
                        {getTypeBadge(v.type_visite)}
                      </div>
                      <p className="text-sm text-[#4B5563]">{getChantierName(v.chantier_id)}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-[#4B5563]">
                        <span className="flex items-center gap-1">
                          <CalendarBlank size={14} />
                          {v.date_visite ? new Date(v.date_visite).toLocaleDateString('fr-FR') : 'Non planifiee'}
                        </span>
                        <span>{getStatusLabel(v.status)}</span>
                      </div>
                      {v.participants?.length > 0 && (
                        <p className="text-xs text-[#4B5563] mt-1">{v.participants.length} participant(s)</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="icon" onClick={() => openEdit(v)} className="h-12 w-12">
                      <PencilSimple size={20} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setDeleteConfirm(v)}
                      className="h-12 w-12 text-red-600 hover:bg-red-50">
                      <Trash size={20} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="chantix-card">
              <CardContent className="py-12 text-center">
                <Binoculars size={48} className="mx-auto mb-4 text-[#FF6B00] opacity-50" />
                <h3 className="font-semibold text-[#111827] mb-2">Aucune visite</h3>
                <p className="text-[#4B5563] mb-4">
                  {chantiers.length === 0 ? 'Creez d\'abord un chantier CSPS' : 'Planifiez votre premiere visite'}
                </p>
                {chantiers.length > 0 && (
                  <Button className="bg-[#FF6B00] hover:bg-orange-700" onClick={openCreate}>
                    <Plus size={20} className="mr-2" /> Planifier une visite
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <VisiteFormSheet
        open={isSheetOpen} onOpenChange={setIsSheetOpen}
        editing={editing} chantiers={chantiers}
        formData={formData} setFormData={setFormData}
        submitting={submitting} onSubmit={handleSubmit}
      />

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimér cette visite ?</AlertDialogTitle>
            <AlertDialogDescription>
              Supprimér la visite "{deleteConfirm?.objet}" ? Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)}
              className="h-12 bg-red-600 hover:bg-red-700">Supprimér</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
